import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

export interface PickupSlotCapacityOverride {
  id: string;
  pickupSlotId: string;
  /** ISO `YYYY-MM-DD`. */
  date: string;
  maxCapacity: number;
  createdAt: string;
  updatedAt: string;
}

export type PickupServiceDay =
  | "MON"
  | "TUE"
  | "WED"
  | "THU"
  | "FRI"
  | "SAT"
  | "SUN";

export interface PickupSlot {
  id: string;
  carrierAddOnServiceId: string;
  dispatchCenterId: string;
  name: string;
  /** "HH:mm" 24-hour, dispatch-local tz. */
  startTime: string;
  endTime: string;
  serviceDays: PickupServiceDay[];
  bookingLeadMinutes: number;
  /** null = unlimited. */
  defaultCapacity: number | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  dispatchCenter?: { id: string; code: string; name: string };
  capacityOverrides?: PickupSlotCapacityOverride[];
}

export interface CreatePickupSlotInput {
  dispatchCenterId: string;
  name: string;
  startTime: string;
  endTime: string;
  serviceDays: PickupServiceDay[];
  bookingLeadMinutes?: number;
  defaultCapacity?: number | null;
  isActive?: boolean;
  sortOrder?: number;
}

export type UpdatePickupSlotInput = Partial<CreatePickupSlotInput>;

// ─── Read ──────────────────────────────────────────────────────────────────

export function useListPickupSlots(
  serviceId: string | null,
  filters?: { dispatchCenterId?: string }
) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["pickup-slots", serviceId, filters],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: PickupSlot[];
      }>(`${API_URL}/api/v2/admin/pickup-services/${serviceId}/slots`, {
        params: filters,
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    enabled: !!token && !!serviceId,
  });
}

// ─── Slot CRUD ─────────────────────────────────────────────────────────────

export function useCreatePickupSlot(
  serviceId: string,
  options?: {
    onSuccess?: (data: PickupSlot) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePickupSlotInput) => {
      const { data } = await axios.post<{ success: boolean; data: PickupSlot }>(
        `${API_URL}/api/v2/admin/pickup-services/${serviceId}/slots`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-slots"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdatePickupSlot(options?: {
  onSuccess?: (data: PickupSlot) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      slotId,
      data: body,
    }: {
      slotId: string;
      data: UpdatePickupSlotInput;
    }) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: PickupSlot;
      }>(`${API_URL}/api/v2/admin/pickup-slots/${slotId}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-slots"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeletePickupSlot(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slotId: string) => {
      await axios.delete(`${API_URL}/api/v2/admin/pickup-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-slots"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

// ─── Capacity overrides ────────────────────────────────────────────────────

export function useUpsertPickupSlotCapacityOverride(options?: {
  onSuccess?: (data: PickupSlotCapacityOverride) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      slotId: string;
      date: string;
      maxCapacity: number;
    }) => {
      const { data } = await axios.post<{
        success: boolean;
        data: PickupSlotCapacityOverride;
      }>(
        `${API_URL}/api/v2/admin/pickup-slots/${input.slotId}/capacity-overrides`,
        { date: input.date, maxCapacity: input.maxCapacity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-slots"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeletePickupSlotCapacityOverride(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (overrideId: string) => {
      await axios.delete(
        `${API_URL}/api/v2/admin/pickup-slot-capacity-overrides/${overrideId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-slots"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}
